import { LoadState } from '@/ui/LoadState';
/**
 * TrendingPanel — right-rail trending hashtags.
 *
 * Reads useTrends().trends and renders each Trend as a row with
 * tag + post-count. Wrapped in a ConsolePanel with a heading.
 */
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Panel, SectionLabel } from '@/ui';
import { useTrends } from '@/hooks';
import { PanelEmpty } from './PanelEmpty';
import type { Trend } from '@/core/models';

/* ── Styled helpers ───────────────────────────────────────────────────────── */

const ViewAll = styled(Link)`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
  &:hover {
    color: ${({ theme }) => theme.color.textPrimary};
  }
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Item = styled.li`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

// A trend is a hashtag search (1.24.00 B3): the row links to /explore?q=%23tag,
// which seeds the Explore box with "#tag" and opens the server results view.
const Tag = styled(Link)`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const Count = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

/* ── Component ────────────────────────────────────────────────────────────── */

function TrendRow({ trend }: { trend: Trend }) {
  const bare = trend.tag.replace(/^#/, '');
  return (
    <Item>
      <Tag to={`/explore?q=${encodeURIComponent(`#${bare}`)}`}>{trend.tag}</Tag>
      <Count>{trend.count}</Count>
    </Item>
  );
}

export function TrendingPanel() {
  const { trends, ...state } = useTrends();
  if (state.loading || state.error)
    return (
      <Panel>
        <SectionLabel as="h2">Trending</SectionLabel>
        <LoadState {...state} label="trends" />
      </Panel>
    );
  return <TrendingPanelView trends={trends} />;
}

export function TrendingPanelView({ trends }: { trends: Trend[] }) {
  return (
    <Panel>
      <SectionLabel
        as="h2"
        action={
          <ViewAll to="/explore" aria-label="View all trends">
            View all
          </ViewAll>
        }
      >
        Trending
      </SectionLabel>
      {trends.length === 0 ? (
        <PanelEmpty>Nothing trending yet.</PanelEmpty>
      ) : (
        <List>
          {trends.slice(0, 5).map((trend) => (
            <TrendRow key={trend.tag} trend={trend} />
          ))}
        </List>
      )}
    </Panel>
  );
}
